import React from 'react';
import PropTypes from 'prop-types';
import { Badge, Card } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import InstitutionLogo from '@/components/common/InstitutionLogo';
import {
    REQUEST_STATUS_LABELS,
    REQUEST_STATUS_VARIANTS,
} from '@/shared/constants/requests';
import { formatDenverDateTime } from '@/shared/utils/denverDates';

// The card title is a heading, so its level has to follow the page it sits
// on. The panel is a section under the page h1, which makes h3 the default.
function RequestCard({ request, titleAs = 'h3', children }) {
    const requester = request.requester;
    const hasLogistics = Boolean(request.preferredWindow || request.deliverTo);
    return (
        <Card className="mb-3">
            <Card.Body>
                <Card.Title as={titleAs} className="h6 mb-1">
                    {requester.name}
                </Card.Title>
                <div className="mb-2">
                    <InstitutionLogo
                        institution={requester.institution}
                        className="me-1"
                    />
                    <Badge bg={REQUEST_STATUS_VARIANTS[request.status]}>
                        {REQUEST_STATUS_LABELS[request.status] ||
                            request.status}
                    </Badge>
                </div>
                {requester.department && (
                    <p className="text-muted mb-1">{requester.department}</p>
                )}
                {/* When the request was made, so an owner choosing between
                    several can see who asked first. */}
                {request.createdAt && (
                    <p className="text-muted mb-1">
                        Requested{' '}
                        <time dateTime={request.createdAt}>
                            {formatDenverDateTime(request.createdAt)}
                        </time>
                    </p>
                )}
                {requester.email && (
                    <p className="mb-1">
                        <a href={`mailto:${requester.email}`}>
                            {requester.email}
                        </a>
                    </p>
                )}
                {requester.phone && <p className="mb-2">{requester.phone}</p>}
                <p className="mb-2">{request.note}</p>
                {hasLogistics && (
                    <dl className="row mb-2">
                        {request.preferredWindow && (
                            <>
                                <dt className="col-sm-5">Preferred window</dt>
                                <dd className="col-sm-7">
                                    {request.preferredWindow}
                                </dd>
                            </>
                        )}
                        {request.deliverTo && (
                            <>
                                <dt className="col-sm-5">Deliver to</dt>
                                <dd className="col-sm-7">
                                    {request.deliverTo}
                                </dd>
                            </>
                        )}
                    </dl>
                )}
                {/* Several cards carry the same link text, so the name of the
                    person it opens is what tells them apart out of context. */}
                <Link
                    to={`/requests/${request._id}`}
                    aria-label={`Open thread with ${requester.name}`}
                >
                    Open thread
                </Link>
                {children && (
                    <div className="d-flex gap-2 mt-3">{children}</div>
                )}
            </Card.Body>
        </Card>
    );
}

RequestCard.propTypes = {
    request: PropTypes.object.isRequired,
    titleAs: PropTypes.string,
    children: PropTypes.node,
};

export default RequestCard;
